/* Tankenöts-hörna — puzzle hub with Sudoku, Memory, Ordagatan & AI-Gåtor.
 * ADHD-friendly dopamine breaks — small, finished, kawaii. */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Brain, Grid3x3, Layers, Type, HelpCircle, ArrowLeft, RotateCcw, Trophy, Clock, Sparkles, Loader2, Eye, X } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ============== SUDOKU 4x4 / 9x9 ==============
function generateSudoku(size = 4, holes = 6) {
  const N = size;
  const grid = Array.from({ length: N }, () => Array(N).fill(0));
  const box = Math.sqrt(N);
  const valid = (r, c, v) => {
    for (let i = 0; i < N; i++) {
      if (grid[r][i] === v || grid[i][c] === v) return false;
    }
    const br = Math.floor(r / box) * box;
    const bc = Math.floor(c / box) * box;
    for (let i = 0; i < box; i++) for (let j = 0; j < box; j++) {
      if (grid[br + i][bc + j] === v) return false;
    }
    return true;
  };
  const fill = (idx = 0) => {
    if (idx >= N * N) return true;
    const r = Math.floor(idx / N), c = idx % N;
    const nums = Array.from({ length: N }, (_, i) => i + 1).sort(() => Math.random() - 0.5);
    for (const v of nums) {
      if (valid(r, c, v)) {
        grid[r][c] = v;
        if (fill(idx + 1)) return true;
        grid[r][c] = 0;
      }
    }
    return false;
  };
  fill();
  const solution = grid.map((r) => [...r]);
  const puzzle = grid.map((r) => [...r]);
  let removed = 0;
  while (removed < holes) {
    const r = Math.floor(Math.random() * N);
    const c = Math.floor(Math.random() * N);
    if (puzzle[r][c] !== 0) { puzzle[r][c] = 0; removed++; }
  }
  return { puzzle, solution };
}

function SudokuGame({ onBack, onWin }) {
  const [size, setSize] = useState(4);
  const [holes, setHoles] = useState(6);
  const [puzzle, setPuzzle] = useState(null);
  const [grid, setGrid] = useState(null);
  const [start, setStart] = useState(Date.now());
  const [won, setWon] = useState(false);

  const startNew = useCallback((s = size, h = holes) => {
    const { puzzle: p, solution } = generateSudoku(s, h);
    setPuzzle({ initial: p, solution });
    setGrid(p.map((r) => [...r]));
    setStart(Date.now());
    setWon(false);
  }, [size, holes]);

  useEffect(() => { startNew(size, holes); }, [size, holes, startNew]);

  const setCell = (r, c, v) => {
    if (!puzzle || puzzle.initial[r][c] !== 0) return;
    const next = grid.map((row) => [...row]);
    next[r][c] = v;
    setGrid(next);
    // Check win
    if (next.flat().join(',') === puzzle.solution.flat().join(',')) {
      setWon(true);
      const seconds = Math.round((Date.now() - start) / 1000);
      onWin?.({ puzzle: 'sudoku', won: true, time_seconds: seconds, difficulty: size === 4 ? 'easy' : 'medium', score: Math.max(0, 1000 - seconds * 5) });
      toast.success(`🎉 Klar på ${seconds}s!`);
    }
  };

  if (!grid) return null;
  const N = size;
  const box = Math.sqrt(N);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <button onClick={onBack} data-testid="puzzle-back" className="px-3 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-sm flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> Tillbaka</button>
        <div className="flex gap-2">
          <button onClick={() => { setSize(4); setHoles(6); }} data-testid="sudoku-easy" className={`px-3 py-1 rounded-lg text-xs font-bold ${size === 4 ? 'bg-emerald-500 text-white' : 'bg-white border border-gray-200'}`}>4×4 Lätt</button>
          <button onClick={() => { setSize(9); setHoles(40); }} data-testid="sudoku-hard" className={`px-3 py-1 rounded-lg text-xs font-bold ${size === 9 ? 'bg-emerald-500 text-white' : 'bg-white border border-gray-200'}`}>9×9 Klassisk</button>
          <button onClick={() => startNew(size, holes)} data-testid="sudoku-new" className="px-3 py-1 rounded-lg text-xs font-bold bg-white border border-gray-200 flex items-center gap-1"><RotateCcw className="w-3 h-3" /> Ny</button>
        </div>
      </div>
      <div
        className={`grid gap-0.5 mx-auto bg-violet-700/70 p-1 rounded-xl ${N === 4 ? 'w-72' : 'w-full max-w-lg'}`}
        style={{ gridTemplateColumns: `repeat(${N}, minmax(0,1fr))` }}
        data-testid="sudoku-grid"
      >
        {grid.map((row, r) => row.map((v, c) => {
          const fixed = puzzle?.initial[r][c] !== 0;
          const correct = v === puzzle?.solution[r][c];
          const inBorder = ((r + 1) % box === 0 && r < N - 1) || ((c + 1) % box === 0 && c < N - 1);
          return (
            <button
              key={`${r}-${c}`}
              data-testid={`sudoku-cell-${r}-${c}`}
              onClick={() => {
                if (fixed || won) return;
                const next = ((v || 0) + 1) % (N + 1);
                setCell(r, c, next);
              }}
              className={`aspect-square ${N === 4 ? 'text-2xl' : 'text-sm'} font-bold rounded ${
                fixed ? 'bg-violet-200 text-violet-900 cursor-not-allowed' :
                v === 0 ? 'bg-white hover:bg-violet-50 text-gray-400' :
                correct ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
              } ${inBorder ? 'mr-0.5 mb-0.5' : ''}`}
            >
              {v || ''}
            </button>
          );
        }))}
      </div>
      {won && (
        <div className="bg-emerald-100 border border-emerald-300 rounded-xl p-3 text-center text-emerald-800 font-bold text-sm" data-testid="sudoku-won">
          🎉 Bra jobbat! Klart på {Math.round((Date.now() - start) / 1000)}s
        </div>
      )}
      <p className="text-[10px] text-gray-400 italic text-center">💡 Klicka på en ruta för att öka siffran (klickar du flera gånger så går du runt)</p>
    </div>
  );
}

// ============== MEMORY ==============
const MEMORY_EMOJIS = ['🐼', '🐱', '🦊', '🐰', '🦋', '🌸', '🍓', '🌈', '✨', '🐢', '🐧', '🦉'];

function MemoryGame({ onBack, onWin }) {
  const [pairs, setPairs] = useState(8);
  const [cards, setCards] = useState([]);
  const [flipped, setFlipped] = useState([]);
  const [matched, setMatched] = useState([]);
  const [moves, setMoves] = useState(0);
  const [start, setStart] = useState(Date.now());

  const startNew = useCallback((p = pairs) => {
    const emojis = [...MEMORY_EMOJIS].sort(() => Math.random() - 0.5).slice(0, p);
    const deck = [...emojis, ...emojis].sort(() => Math.random() - 0.5).map((e, i) => ({ id: i, emoji: e }));
    setCards(deck);
    setFlipped([]);
    setMatched([]);
    setMoves(0);
    setStart(Date.now());
  }, [pairs]);

  useEffect(() => { startNew(pairs); }, [pairs, startNew]);

  const click = (id) => {
    if (flipped.includes(id) || matched.includes(id)) return;
    if (flipped.length === 2) return;
    const next = [...flipped, id];
    setFlipped(next);
    if (next.length === 2) {
      setMoves((m) => m + 1);
      const [a, b] = next.map((i) => cards.find((c) => c.id === i));
      if (a.emoji === b.emoji) {
        setTimeout(() => {
          setMatched((m) => {
            const nm = [...m, a.id, b.id];
            if (nm.length === cards.length) {
              const seconds = Math.round((Date.now() - start) / 1000);
              const score = Math.max(0, 1000 - moves * 10 - seconds * 2);
              onWin?.({ puzzle: 'memory', won: true, time_seconds: seconds, score, extra: { moves: moves + 1, pairs } });
              toast.success(`🎉 Klar! ${moves + 1} drag · ${seconds}s`);
            }
            return nm;
          });
          setFlipped([]);
        }, 600);
      } else {
        setTimeout(() => setFlipped([]), 900);
      }
    }
  };

  const isComplete = matched.length > 0 && matched.length === cards.length;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <button onClick={onBack} data-testid="puzzle-back" className="px-3 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-sm flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> Tillbaka</button>
        <div className="flex gap-2 items-center">
          <span className="text-xs text-gray-500">Drag: <strong>{moves}</strong></span>
          {[6, 8, 10, 12].map((p) => (
            <button key={p} onClick={() => setPairs(p)} data-testid={`memory-${p}`} className={`px-2 py-1 rounded-lg text-xs font-bold ${pairs === p ? 'bg-pink-500 text-white' : 'bg-white border border-gray-200'}`}>{p}</button>
          ))}
          <button onClick={() => startNew(pairs)} data-testid="memory-new" className="px-2 py-1 rounded-lg text-xs font-bold bg-white border border-gray-200"><RotateCcw className="w-3 h-3" /></button>
        </div>
      </div>
      <div className="grid grid-cols-4 sm:grid-cols-5 gap-2 max-w-md mx-auto" data-testid="memory-grid">
        {cards.map((c) => {
          const isFlipped = flipped.includes(c.id) || matched.includes(c.id);
          const isMatched = matched.includes(c.id);
          return (
            <button
              key={c.id}
              onClick={() => click(c.id)}
              data-testid={`memory-card-${c.id}`}
              className={`aspect-square rounded-2xl text-4xl font-bold transition-all duration-300 ${
                isMatched ? 'bg-emerald-100 scale-95 opacity-60' :
                isFlipped ? 'bg-white shadow-lg scale-105' :
                'bg-gradient-to-br from-pink-300 to-rose-400 hover:from-pink-400 hover:to-rose-500 shadow'
              }`}
            >
              {isFlipped ? c.emoji : ''}
            </button>
          );
        })}
      </div>
      {isComplete && (
        <div className="bg-emerald-100 border border-emerald-300 rounded-xl p-3 text-center text-emerald-800 font-bold text-sm" data-testid="memory-won">
          🎉 Klart! {moves} drag · {Math.round((Date.now() - start) / 1000)}s
        </div>
      )}
    </div>
  );
}

// ============== ORDAGATAN (Wordle-style) ==============
function OrdagatanGame({ onBack, onWin }) {
  const [target, setTarget] = useState(null);
  const [guesses, setGuesses] = useState([]);
  const [input, setInput] = useState('');
  const [done, setDone] = useState(null); // null | 'won' | 'lost'
  const [start, setStart] = useState(Date.now());
  const MAX = 6;

  const loadWord = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/puzzles/word-of-day`, { withCredentials: true });
      setTarget((r.data?.word || '').toLowerCase());
      setGuesses([]);
      setInput('');
      setDone(null);
      setStart(Date.now());
    } catch {
      toast.error('Kunde inte ladda dagens ord');
    }
  }, []);

  useEffect(() => { loadWord(); }, [loadWord]);

  const submit = () => {
    if (!target || done) return;
    const g = input.trim().toLowerCase();
    if (g.length !== 5) { toast.info('Ordet måste vara 5 bokstäver'); return; }
    const result = [];
    const targetChars = target.split('');
    for (let i = 0; i < 5; i++) {
      if (g[i] === target[i]) { result.push('hit'); targetChars[i] = '_'; }
      else result.push(null);
    }
    for (let i = 0; i < 5; i++) {
      if (result[i] === null) {
        const idx = targetChars.indexOf(g[i]);
        if (idx >= 0) { result[i] = 'present'; targetChars[idx] = '_'; }
        else result[i] = 'miss';
      }
    }
    const next = [...guesses, { word: g, result }];
    setGuesses(next);
    setInput('');
    if (result.every((r) => r === 'hit')) {
      setDone('won');
      const seconds = Math.round((Date.now() - start) / 1000);
      onWin?.({ puzzle: 'wordle', won: true, time_seconds: seconds, score: Math.max(0, (MAX - next.length + 1) * 100) });
    } else if (next.length >= MAX) {
      setDone('lost');
      onWin?.({ puzzle: 'wordle', won: false, time_seconds: Math.round((Date.now() - start) / 1000) });
    }
  };

  const colorFor = (r) => r === 'hit' ? 'bg-emerald-400 text-white' : r === 'present' ? 'bg-amber-300 text-amber-900' : 'bg-gray-300 text-gray-700';

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <button onClick={onBack} data-testid="puzzle-back" className="px-3 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-sm flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> Tillbaka</button>
        <p className="text-xs text-gray-500">Dagens 5-bokstavsord på svenska · {guesses.length}/{MAX}</p>
      </div>
      <div className="max-w-xs mx-auto space-y-1" data-testid="ordagatan-board">
        {Array.from({ length: MAX }).map((_, row) => {
          const g = guesses[row];
          return (
            <div key={row} className="grid grid-cols-5 gap-1">
              {Array.from({ length: 5 }).map((_, col) => (
                <div key={col} className={`aspect-square rounded-lg flex items-center justify-center font-bold text-lg uppercase ${g ? colorFor(g.result[col]) : 'bg-white border-2 border-gray-200'}`}>
                  {g ? g.word[col] : ''}
                </div>
              ))}
            </div>
          );
        })}
      </div>
      {done === 'won' && <div className="bg-emerald-100 rounded-xl p-3 text-center text-emerald-800 font-bold" data-testid="ordagatan-won">🎉 Klart!</div>}
      {done === 'lost' && <div className="bg-rose-100 rounded-xl p-3 text-center text-rose-800 font-bold">😢 Slut! Ordet var <strong>{target}</strong></div>}
      {!done && (
        <div className="flex gap-2 max-w-xs mx-auto">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value.toLowerCase().slice(0, 5))}
            onKeyDown={(e) => e.key === 'Enter' && submit()}
            placeholder="abcde"
            data-testid="ordagatan-input"
            className="flex-1 px-3 py-2 rounded-xl border-2 border-pink-200 focus:border-pink-500 outline-none text-base font-mono uppercase"
            maxLength={5}
            autoFocus
          />
          <button onClick={submit} data-testid="ordagatan-submit" className="px-4 py-2 rounded-xl bg-pink-500 hover:bg-pink-600 text-white font-bold text-sm">Gissa</button>
        </div>
      )}
    </div>
  );
}

// ============== AI GÅTOR ==============
function RiddleGame({ onBack, onWin }) {
  const [riddle, setRiddle] = useState(null);
  const [showAnswer, setShowAnswer] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const [busy, setBusy] = useState(false);
  const [difficulty, setDifficulty] = useState('medium');

  const load = useCallback(async () => {
    setBusy(true);
    setShowAnswer(false);
    setShowHint(false);
    try {
      const r = await axios.post(`${API}/puzzles/riddle`, { difficulty }, { withCredentials: true });
      setRiddle(r.data);
    } catch (e) {
      toast.error('Kunde inte hämta gåta: ' + (e?.response?.data?.detail || ''));
    } finally {
      setBusy(false);
    }
  }, [difficulty]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <button onClick={onBack} data-testid="puzzle-back" className="px-3 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-sm flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> Tillbaka</button>
        <div className="flex gap-1.5">
          {['easy', 'medium', 'hard'].map((d) => (
            <button key={d} onClick={() => setDifficulty(d)} data-testid={`riddle-diff-${d}`} className={`px-2.5 py-1 rounded-lg text-xs font-bold ${difficulty === d ? 'bg-amber-500 text-white' : 'bg-white border border-gray-200'}`}>
              {d === 'easy' ? 'Lätt' : d === 'medium' ? 'Medel' : 'Svår'}
            </button>
          ))}
          <button onClick={load} disabled={busy} data-testid="riddle-new" className="px-2.5 py-1 rounded-lg text-xs font-bold bg-white border border-gray-200">
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
          </button>
        </div>
      </div>
      <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-3xl p-6 border-2 border-amber-200 shadow-sm min-h-[200px]" data-testid="riddle-card">
        {busy && <div className="flex items-center justify-center h-32 text-amber-500"><Loader2 className="w-8 h-8 animate-spin" /></div>}
        {!busy && riddle && (
          <div className="space-y-4">
            <p className="text-base sm:text-lg text-amber-900 leading-relaxed font-medium" data-testid="riddle-question">
              {riddle.question}
            </p>
            {!showHint && !showAnswer && riddle.hint && (
              <button onClick={() => setShowHint(true)} data-testid="riddle-hint-btn" className="text-xs text-amber-700 hover:text-amber-800 hover:underline flex items-center gap-1">
                <HelpCircle className="w-3.5 h-3.5" /> Vill du ha en ledtråd?
              </button>
            )}
            {showHint && !showAnswer && (
              <div className="bg-white/70 rounded-xl px-3 py-2 text-sm italic text-amber-800" data-testid="riddle-hint">
                💡 {riddle.hint}
              </div>
            )}
            {!showAnswer && (
              <button onClick={() => setShowAnswer(true)} data-testid="riddle-show-answer" className="w-full px-4 py-2 rounded-xl bg-amber-500 hover:bg-amber-600 text-white font-bold text-sm flex items-center justify-center gap-1.5">
                <Eye className="w-4 h-4" /> Visa svaret
              </button>
            )}
            {showAnswer && (
              <div className="bg-emerald-100 rounded-xl px-3 py-2 text-base font-bold text-emerald-800 text-center" data-testid="riddle-answer">
                ✨ {riddle.answer}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ============== HUB ==============
const GAMES = [
  { id: 'sudoku',    label: 'Sudoku',     emoji: '🔢', icon: Grid3x3, color: 'from-violet-200 to-violet-300', desc: '4×4 lätt eller 9×9 klassisk' },
  { id: 'memory',    label: 'Memory',     emoji: '🎴', icon: Layers,  color: 'from-pink-200 to-rose-300',    desc: 'Hitta kawaii-paren' },
  { id: 'ordagatan', label: 'Ordagatan',  emoji: '🔤', icon: Type,    color: 'from-emerald-200 to-teal-300', desc: 'Dagens svenska 5-bokstavsord' },
  { id: 'riddle',    label: 'AI-Gåtor',   emoji: '🧠', icon: HelpCircle, color: 'from-amber-200 to-orange-300', desc: 'AI bygger en ny gåta' },
];

export default function PuzzlesPage() {
  const [game, setGame] = useState(null);
  const [stats, setStats] = useState({});

  const loadStats = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/puzzles/stats`, { withCredentials: true });
      setStats(r.data?.stats || {});
    } catch {/**/}
  }, []);

  useEffect(() => { loadStats(); }, [loadStats]);

  const saveScore = async (data) => {
    try {
      await axios.post(`${API}/puzzles/score`, data, { withCredentials: true });
      loadStats();
    } catch {/**/}
  };

  const back = () => setGame(null);

  return (
    <div className="space-y-4" data-testid="puzzles-page">
      {!game && (
        <>
          <div className="bg-gradient-to-br from-violet-100 via-pink-50 to-amber-100 rounded-3xl p-5 shadow-sm border border-violet-200">
            <h1 className="text-2xl font-bold text-violet-800 flex items-center gap-2">
              <Brain className="w-6 h-6" /> Tankenöts-hörna
            </h1>
            <p className="text-sm text-violet-700/70 italic">
              Korta dopamin-pauser för hjärnan 💛 — finita, lugna, lagom mycket
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {GAMES.map((g) => {
              const s = stats[g.id];
              return (
                <button
                  key={g.id}
                  onClick={() => setGame(g.id)}
                  data-testid={`puzzle-open-${g.id}`}
                  className={`bg-gradient-to-br ${g.color} rounded-2xl p-5 shadow hover:shadow-lg transition border-2 border-white/50 hover:scale-[1.02] text-left`}
                >
                  <div className="flex items-start gap-3">
                    <div className="text-5xl">{g.emoji}</div>
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-gray-800">{g.label}</h3>
                      <p className="text-xs text-gray-700/80">{g.desc}</p>
                      {s && (
                        <div className="flex gap-2 mt-2 flex-wrap">
                          <span className="text-[10px] bg-white/70 px-2 py-0.5 rounded-full font-bold text-gray-700 flex items-center gap-0.5">
                            <Trophy className="w-2.5 h-2.5" /> {s.wins}/{s.plays}
                          </span>
                          {s.best_time && (
                            <span className="text-[10px] bg-white/70 px-2 py-0.5 rounded-full font-bold text-gray-700 flex items-center gap-0.5">
                              <Clock className="w-2.5 h-2.5" /> {s.best_time}s bäst
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
          <p className="text-[10px] text-gray-400 italic text-center">
            💡 Tankenöts-pauser är perfekta när du behöver byta spår men inte vill stänga av hjärnan helt
          </p>
        </>
      )}
      {game === 'sudoku' && <SudokuGame onBack={back} onWin={saveScore} />}
      {game === 'memory' && <MemoryGame onBack={back} onWin={saveScore} />}
      {game === 'ordagatan' && <OrdagatanGame onBack={back} onWin={saveScore} />}
      {game === 'riddle' && <RiddleGame onBack={back} onWin={saveScore} />}
    </div>
  );
}
